<template>
  <div
    :class="[
      $style.newBuildingStickyBar,
      isHidden ? $style.hidden : '',
      isPlatformIs24() ? $style.isPlatformIs24 : '',
    ]"
  >
    <div :class="$style.icon">
      <ConstructionCraneGreen v-if="isPlatformIs24()" />
      <ConstructionCrane v-else />
    </div>
    <div :class="$style.textAndLink">
      <div :class="$style.text">{{ t('$domus.badge.newConstruction.long') }}</div>
      <div :class="$style.link">
        <template v-if="newProjectInfo.projectUrl">
          <HgLink
            ref="ncProjectUrl"
            :href="newProjectInfo.projectUrl"
            target="_blank"
            @click="
              trackNewConstructionProjectLink(
                newProjectInfo.projectUrl || '',
                t('newProjectCard.website'),
              )
            "
          >
            {{ t('buttons.projectWebsiteLink') }}
          </HgLink>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgLink } from '@smg-real-estate/domus';
import { defineAsyncComponent } from 'vue';

import { isPlatformIs24 } from '@/helpers/platformDetection';
import { NewConstructionDataWithOtherListings } from '@/models';

import { useI18n } from 'vue-i18n';
import { trackNewConstructionProjectLink } from '../NewBuildingProjectCard/NewBuildingProjectCard.tracking';

const { t } = useI18n();

defineOptions({
  name: 'NewBuildingStickyBar',
});

const ConstructionCraneGreen = defineAsyncComponent(
  () => import('@/assets/NewBuildingProjectCard/construction_crane_green.svg?component'),
);
const ConstructionCrane = defineAsyncComponent(
  () => import('@/assets/NewBuildingProjectCard/construction_crane.svg?component'),
);

interface Props {
  newProjectInfo: NewConstructionDataWithOtherListings;
  isHidden?: boolean;
}
withDefaults(defineProps<Props>(), {
  isHidden: false,
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.newBuildingStickyBar {
  position: absolute;
  right: 0;
  left: 0;
  display: none;
  align-content: center;

  &.hidden {
    display: none;
  }

  .icon {
    display: flex;
    justify-content: center;
    width: 40px;
    height: 47px;
    padding: $space-25 $space-50;
    background: $color-white;
    border: 4px solid $color-orange-20;
    border-radius: 50%;
  }

  .textAndLink {
    position: relative;
    display: flex;
    flex-grow: 1;
    background: $color-orange-20;
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;

    &::before {
      position: absolute;
      left: -30px;
      z-index: -1;
      display: inline;
      width: 32px;
      height: 100%;
      content: '';
      background: $color-orange-20;
    }

    .text {
      display: flex;
      align-items: center;
      padding: $space-50 0;
      margin-left: $space-75;

      @include body-2-semi-bold($color-orange-100);
    }

    .link {
      display: flex;
      align-self: center;
      margin-right: $space-150;
      margin-left: auto;
      cursor: pointer;

      @include body-2-semi-bold($color-blue-60);
    }
  }

  &.isPlatformIs24 {
    .icon {
      border: 4px solid $color-teal-60;
    }

    .textAndLink {
      background: $color-teal-60;

      &::before {
        background: $color-teal-60;
      }

      .text {
        @include body-2-semi-bold($color-teal-140);
      }

      .link {
        @include body-2-semi-bold($color-azul-120);
      }
    }
  }
}

@media (min-width: $viewport-medium-min) {
  .newBuildingStickyBar {
    display: flex;
  }
}
</style>
